import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  IconButton,
  Dialog,
  Divider,
} from '@mui/material';
import {
  LocationOn,
  Phone,
  Email,
  EventSeat,
  ArrowForward,
  ArrowBack,
  Close,
  AccessTime,
  CalendarToday,
  Repeat,
  Schedule,
  LockOutlined,
} from '@mui/icons-material';
import { motion, useReducedMotion } from 'framer-motion';
import {
  formatHours,
  formatOperationalDays,
  formatPrice,
  planPeriodLabel,
  hoursLabel,
  discountedFirstPrice,
  titleCasePlanType,
  formatFullAddress,
  getAmenityIcon,
  PlanFeature,
  Reveal,
  MotionList,
  MotionItem,
  AnimatedNumber,
} from './themeHelpers';

const placeholderImages = [
  'https://images.unsplash.com/photo-1521587760476-6c12a4b040da?w=800&q=80',
  'https://images.unsplash.com/photo-1507842217343-583bb7270b66?w=800&q=80',
  'https://images.unsplash.com/photo-1568667256549-094345857637?w=800&q=80',
];

// Gold underline divider used for section headers in classic theme
function GoldDivider() {
  return <Divider sx={{ width: 80, mx: 'auto', mb: 4, borderColor: '#D4AF37', borderWidth: 2 }} />;
}

function ClassicTheme({ library, plans, accentColor, onBookNow }) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const shouldReduce = useReducedMotion();

  const images = library.gallery?.length > 0 ? library.gallery : placeholderImages;
  const defaultDescription = `Welcome to ${library.name}! We provide a peaceful and productive environment for students and professionals.`;

  const nextImage = () => setCurrentImageIndex((prev) => (prev + 1) % images.length);
  const prevImage = () => setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);

  const hoursStr = formatHours(library.open_time, library.close_time);
  const daysStr = formatOperationalDays(library.operational_days);
  const fullAddress = formatFullAddress(library);

  // Seat availability
  const availSeats = library.available_seats ?? library.capacity;
  const seatDisplay =
    availSeats !== null && availSeats !== undefined && library.capacity
      ? `${availSeats} / ${library.capacity}`
      : library.capacity
      ? String(library.capacity)
      : null;

  // For AnimatedNumber — only the numeric portion
  const availSeatsNum =
    typeof availSeats === 'number' && Number.isFinite(availSeats) ? availSeats : null;
  const lockerNum =
    library.locker_count > 0 && typeof library.locker_count === 'number'
      ? library.locker_count
      : null;

  // Stat boxes: seats, hours, optionally lockers
  const showLockers = library.locker_count > 0;
  const statCols = showLockers ? 4 : 6; // xs grid for 2-or-3 cols

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#FDF8F3', fontFamily: 'Georgia, serif' }}>
      {/* Header */}
      <Box sx={{ bgcolor: '#2C1810', py: 2 }}>
        <Container maxWidth="lg">
          <Typography
            variant="h4"
            sx={{ color: '#D4AF37', fontFamily: 'Georgia, serif', fontWeight: 400, textAlign: 'center' }}
          >
            {library.name}
          </Typography>
        </Container>
      </Box>

      {/* Hero Section */}
      <Box sx={{ position: 'relative', height: { xs: '40vh', md: '60vh' }, overflow: 'hidden' }}>
        <Box
          component="img"
          src={images[currentImageIndex]}
          alt={library.name}
          loading="eager"
          fetchpriority="high"
          decoding="async"
          sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(to bottom, rgba(44,24,16,0.2), rgba(44,24,16,0.6))',
          }}
        />

        {images.length > 1 && (
          <>
            <IconButton
              onClick={prevImage}
              sx={{ position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', bgcolor: 'rgba(255,255,255,0.8)' }}
            >
              <ArrowBack />
            </IconButton>
            <IconButton
              onClick={nextImage}
              sx={{ position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)', bgcolor: 'rgba(255,255,255,0.8)' }}
            >
              <ArrowForward />
            </IconButton>
          </>
        )}

        {/* Hero content — slow elegant fade-in */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 40,
            left: 0,
            right: 0,
            textAlign: 'center',
            color: 'white',
          }}
        >
          {shouldReduce ? (
            <>
              <Typography variant="h6" sx={{ fontFamily: 'Georgia, serif', opacity: 0.9, mb: 2 }}>
                <LocationOn sx={{ verticalAlign: 'middle', mr: 1 }} />
                {library.address}
              </Typography>
              <Button
                variant="contained"
                size="large"
                onClick={() => onBookNow()}
                sx={{
                  bgcolor: '#D4AF37',
                  color: '#2C1810',
                  '&:hover': { bgcolor: '#C19B2C' },
                  fontFamily: 'Georgia, serif',
                  px: 5,
                  py: 1.5,
                }}
              >
                Reserve Your Seat
              </Button>
            </>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.0, ease: 'easeOut' }}
            >
              <Typography variant="h6" sx={{ fontFamily: 'Georgia, serif', opacity: 0.9, mb: 2 }}>
                <LocationOn sx={{ verticalAlign: 'middle', mr: 1 }} />
                {library.address}
              </Typography>
              <Button
                variant="contained"
                size="large"
                onClick={() => onBookNow()}
                sx={{
                  bgcolor: '#D4AF37',
                  color: '#2C1810',
                  transition: 'background-color 0.25s, transform 0.25s, box-shadow 0.25s',
                  '&:hover': {
                    bgcolor: '#C19B2C',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 20px rgba(212,175,55,0.35)',
                  },
                  fontFamily: 'Georgia, serif',
                  px: 5,
                  py: 1.5,
                }}
              >
                Reserve Your Seat
              </Button>
            </motion.div>
          )}
        </Box>
      </Box>

      {/* About Section */}
      <Container maxWidth="md" sx={{ py: 8, textAlign: 'center' }}>
        <Reveal duration={0.7}>
          <Typography variant="overline" sx={{ color: accentColor, letterSpacing: 3 }}>
            Welcome To
          </Typography>
          <Typography variant="h3" sx={{ fontFamily: 'Georgia, serif', mb: 3, color: '#2C1810' }}>
            {library.name}
          </Typography>
          <GoldDivider />
          <Typography variant="body1" sx={{ lineHeight: 2, color: '#5C4A3D', fontFamily: 'Georgia, serif' }}>
            {library.description || defaultDescription}
          </Typography>
        </Reveal>

        {/* Stat boxes */}
        <Reveal delay={0.12} duration={0.7}>
          <Grid container spacing={4} sx={{ mt: 4 }}>
            {/* Seats */}
            {seatDisplay && (
              <Grid item xs={statCols}>
                <Box
                  sx={{
                    p: 3,
                    border: '1px solid #D4AF37',
                    borderRadius: 0,
                    transition: 'background-color 0.3s, box-shadow 0.3s',
                    '&:hover': {
                      bgcolor: 'rgba(212,175,55,0.06)',
                      boxShadow: '0 4px 18px rgba(212,175,55,0.18)',
                    },
                  }}
                >
                  <EventSeat sx={{ fontSize: 40, color: '#D4AF37', mb: 1 }} />
                  <Typography variant="h4" sx={{ fontFamily: 'Georgia, serif', color: '#2C1810' }}>
                    {availSeatsNum !== null ? (
                      <AnimatedNumber value={availSeatsNum} duration={1.4} />
                    ) : (
                      seatDisplay
                    )}
                    {availSeatsNum !== null && library.capacity ? ` / ${library.capacity}` : ''}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {library.available_seats !== null && library.available_seats !== undefined
                      ? 'Seats Free'
                      : 'Seats'}
                  </Typography>
                </Box>
              </Grid>
            )}

            {/* Hours */}
            <Grid item xs={statCols}>
              <Box
                sx={{
                  p: 3,
                  border: '1px solid #D4AF37',
                  borderRadius: 0,
                  transition: 'background-color 0.3s, box-shadow 0.3s',
                  '&:hover': {
                    bgcolor: 'rgba(212,175,55,0.06)',
                    boxShadow: '0 4px 18px rgba(212,175,55,0.18)',
                  },
                }}
              >
                <AccessTime sx={{ fontSize: 40, color: '#D4AF37', mb: 1 }} />
                <Typography variant="h6" sx={{ fontFamily: 'Georgia, serif', color: '#2C1810' }}>
                  {hoursStr || 'Contact for hours'}
                </Typography>
                {daysStr && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {daysStr}
                  </Typography>
                )}
              </Box>
            </Grid>

            {/* Lockers (optional 3rd box) */}
            {showLockers && (
              <Grid item xs={statCols}>
                <Box
                  sx={{
                    p: 3,
                    border: '1px solid #D4AF37',
                    borderRadius: 0,
                    transition: 'background-color 0.3s, box-shadow 0.3s',
                    '&:hover': {
                      bgcolor: 'rgba(212,175,55,0.06)',
                      boxShadow: '0 4px 18px rgba(212,175,55,0.18)',
                    },
                  }}
                >
                  <LockOutlined sx={{ fontSize: 40, color: '#D4AF37', mb: 1 }} />
                  <Typography variant="h4" sx={{ fontFamily: 'Georgia, serif', color: '#2C1810' }}>
                    {lockerNum !== null ? (
                      <AnimatedNumber value={lockerNum} duration={1.4} />
                    ) : (
                      library.locker_count
                    )}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Lockers</Typography>
                </Box>
              </Grid>
            )}
          </Grid>
        </Reveal>
      </Container>

      {/* Plans Section */}
      <Box sx={{ bgcolor: '#2C1810', py: 8 }}>
        <Container maxWidth="lg">
          <Reveal duration={0.7}>
            <Typography
              variant="h3"
              sx={{ fontFamily: 'Georgia, serif', color: '#D4AF37', textAlign: 'center', mb: 2 }}
            >
              Membership Plans
            </Typography>
            <GoldDivider />
          </Reveal>

          {plans.length > 0 ? (
            <MotionList
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                gap: 24,
                marginTop: 16,
              }}
            >
              {plans.map((plan) => {
                const planName = plan.name || titleCasePlanType(plan.plan_type);
                const price = formatPrice(plan.price_cents);
                const period = planPeriodLabel(plan.plan_type);
                const discPct = plan.first_time_discount;
                const discPrice = discountedFirstPrice(plan.price_cents, discPct);

                return (
                  <MotionItem key={plan.id}>
                    <Card
                      sx={{
                        bgcolor: 'transparent',
                        border: '1px solid #D4AF37',
                        borderRadius: 0,
                        height: '100%',
                        position: 'relative',
                        transition: 'background-color 0.3s, box-shadow 0.3s, transform 0.3s',
                        '&:hover': {
                          bgcolor: 'rgba(212,175,55,0.07)',
                          boxShadow: '0 8px 32px rgba(212,175,55,0.2)',
                          transform: 'translateY(-4px)',
                        },
                      }}
                    >
                      {/* Gold ribbon for discount */}
                      {discPct > 0 && (
                        <Typography
                          variant="overline"
                          sx={{
                            display: 'block',
                            textAlign: 'center',
                            color: '#D4AF37',
                            letterSpacing: 2,
                            pt: 1.5,
                            pb: 0,
                            fontSize: '0.65rem',
                          }}
                        >
                          {discPct}% OFF FIRST TIME
                        </Typography>
                      )}

                      <CardContent sx={{ p: 4, textAlign: 'center' }}>
                        {/* Plan name */}
                        <Typography
                          variant="h6"
                          sx={{ color: 'white', fontFamily: 'Georgia, serif', mb: 1 }}
                        >
                          {planName}
                        </Typography>

                        {/* Price */}
                        {price && (
                          <>
                            {discPct > 0 && discPrice !== null ? (
                              <Box sx={{ mb: 1 }}>
                                <Typography
                                  variant="body2"
                                  sx={{ color: 'rgba(255,255,255,0.45)', textDecoration: 'line-through', fontFamily: 'Georgia, serif' }}
                                >
                                  {price}
                                </Typography>
                                <Typography
                                  variant="h3"
                                  sx={{ color: 'white', fontFamily: 'Georgia, serif' }}
                                >
                                  {formatPrice(discPrice)}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{ color: 'rgba(255,255,255,0.55)', fontFamily: 'Georgia, serif' }}
                                >
                                  first time, then {price}
                                </Typography>
                              </Box>
                            ) : (
                              <Typography
                                variant="h3"
                                sx={{ color: 'white', fontFamily: 'Georgia, serif', my: 1 }}
                              >
                                {price}
                              </Typography>
                            )}
                            {period && (
                              <Typography
                                variant="body2"
                                sx={{ color: 'rgba(255,255,255,0.6)', fontFamily: 'Georgia, serif', mb: 1.5 }}
                              >
                                {period}
                              </Typography>
                            )}
                          </>
                        )}

                        {/* Feature rows */}
                        <Box sx={{ textAlign: 'left', mb: 2 }}>
                          {plan.duration_days && (
                            <PlanFeature
                              icon={<CalendarToday />}
                              color="rgba(212,175,55,0.8)"
                            >
                              Valid {plan.duration_days} days
                            </PlanFeature>
                          )}
                          <PlanFeature
                            icon={<Schedule />}
                            color="rgba(212,175,55,0.8)"
                          >
                            {hoursLabel(plan.hours_per_day)}
                          </PlanFeature>
                          {plan.recurring && (
                            <PlanFeature
                              icon={<Repeat />}
                              color="rgba(212,175,55,0.6)"
                            >
                              Auto-renews
                            </PlanFeature>
                          )}
                        </Box>

                        {/* Description */}
                        {plan.description ? (
                          <Typography
                            variant="caption"
                            sx={{
                              color: 'rgba(255,255,255,0.5)',
                              fontFamily: 'Georgia, serif',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              mb: 2,
                            }}
                          >
                            {plan.description}
                          </Typography>
                        ) : null}

                        <Button
                          variant="outlined"
                          onClick={() => onBookNow(plan)}
                          sx={{
                            borderColor: '#D4AF37',
                            color: '#D4AF37',
                            fontFamily: 'Georgia, serif',
                            transition: 'background-color 0.25s, color 0.25s',
                            '&:hover': { bgcolor: '#D4AF37', color: '#2C1810' },
                          }}
                        >
                          Select
                        </Button>
                      </CardContent>
                    </Card>
                  </MotionItem>
                );
              })}
            </MotionList>
          ) : (
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" sx={{ color: 'white', fontFamily: 'Georgia, serif', mb: 3 }}>
                Contact us for membership details
              </Typography>
              <Button
                variant="contained"
                onClick={() => onBookNow()}
                sx={{ bgcolor: '#D4AF37', color: '#2C1810', '&:hover': { bgcolor: '#C19B2C' } }}
              >
                Get in Touch
              </Button>
            </Box>
          )}
        </Container>
      </Box>

      {/* Gallery Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Reveal duration={0.7}>
          <Typography variant="h3" sx={{ fontFamily: 'Georgia, serif', textAlign: 'center', mb: 2, color: '#2C1810' }}>
            Our Space
          </Typography>
          <GoldDivider />
        </Reveal>
        <MotionList
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
            gap: 16,
          }}
        >
          {images.slice(0, 6).map((img, idx) => (
            <MotionItem key={idx}>
              <Box
                component="img"
                src={img}
                alt={`Gallery ${idx + 1}`}
                onClick={() => { setCurrentImageIndex(idx); setLightboxOpen(true); }}
                loading="lazy"
                decoding="async"
                sx={{
                  width: '100%',
                  height: 220,
                  objectFit: 'cover',
                  display: 'block',
                  cursor: 'pointer',
                  filter: 'sepia(20%)',
                  transition: 'filter 0.4s ease, transform 0.4s ease, box-shadow 0.4s ease',
                  '&:hover': {
                    filter: 'sepia(0%)',
                    transform: 'scale(1.03)',
                    boxShadow: '0 8px 28px rgba(44,24,16,0.22)',
                  },
                }}
              />
            </MotionItem>
          ))}
        </MotionList>
      </Container>

      {/* Amenities */}
      {library.amenities?.length > 0 && (
        <Box sx={{ bgcolor: '#F5EDE4', py: 8 }}>
          <Container maxWidth="lg">
            <Reveal duration={0.7}>
              <Typography variant="h3" sx={{ fontFamily: 'Georgia, serif', textAlign: 'center', mb: 2, color: '#2C1810' }}>
                Facilities
              </Typography>
              <GoldDivider />
            </Reveal>
            <MotionList
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
                gap: 16,
              }}
            >
              {library.amenities.map((amenity, idx) => (
                <MotionItem key={idx}>
                  <Box
                    sx={{
                      textAlign: 'center',
                      p: 2,
                      transition: 'background-color 0.3s, box-shadow 0.3s',
                      '&:hover': {
                        bgcolor: 'rgba(212,175,55,0.08)',
                        boxShadow: '0 4px 16px rgba(212,175,55,0.14)',
                      },
                    }}
                  >
                    <Box sx={{ color: '#D4AF37', mb: 1 }}>
                      {React.cloneElement(getAmenityIcon(amenity), { sx: { fontSize: 36 } })}
                    </Box>
                    <Typography variant="body2" sx={{ fontFamily: 'Georgia, serif', color: '#2C1810' }}>
                      {amenity}
                    </Typography>
                  </Box>
                </MotionItem>
              ))}
            </MotionList>
          </Container>
        </Box>
      )}

      {/* Location & Contact */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Reveal delay={0.0} duration={0.7}>
              <Typography variant="h4" sx={{ fontFamily: 'Georgia, serif', mb: 4, color: '#2C1810' }}>
                Find Us
              </Typography>
              <Box sx={{ height: 300, bgcolor: '#E8DFD3' }}>
                {library.lat && library.lng ? (
                  <iframe
                    title="Location"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    loading="lazy"
                    src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=${library.lat},${library.lng}&zoom=15`}
                  />
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                    <Typography color="text.secondary">Map unavailable</Typography>
                  </Box>
                )}
              </Box>
            </Reveal>
          </Grid>
          <Grid item xs={12} md={6}>
            <Reveal delay={0.14} duration={0.7}>
              <Typography variant="h4" sx={{ fontFamily: 'Georgia, serif', mb: 4, color: '#2C1810' }}>
                Contact
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <LocationOn sx={{ color: '#D4AF37', mt: 0.25 }} />
                  <Typography sx={{ fontFamily: 'Georgia, serif' }}>{fullAddress || library.address}</Typography>
                </Box>
                {library.contact_phone && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Phone sx={{ color: '#D4AF37' }} />
                    <Typography sx={{ fontFamily: 'Georgia, serif' }}>
                      <a href={`tel:${library.contact_phone}`} style={{ color: 'inherit', textDecoration: 'none' }}>
                        {library.contact_phone}
                      </a>
                    </Typography>
                  </Box>
                )}
                {library.contact_email && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Email sx={{ color: '#D4AF37' }} />
                    <Typography sx={{ fontFamily: 'Georgia, serif' }}>
                      <a href={`mailto:${library.contact_email}`} style={{ color: 'inherit', textDecoration: 'none' }}>
                        {library.contact_email}
                      </a>
                    </Typography>
                  </Box>
                )}
              </Box>
              <Button
                variant="contained"
                size="large"
                onClick={() => onBookNow()}
                sx={{
                  mt: 4,
                  bgcolor: '#2C1810',
                  transition: 'background-color 0.25s, transform 0.25s, box-shadow 0.25s',
                  '&:hover': {
                    bgcolor: '#1A0F0A',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 20px rgba(44,24,16,0.25)',
                  },
                  fontFamily: 'Georgia, serif',
                }}
              >
                Reserve Your Seat
              </Button>
            </Reveal>
          </Grid>
        </Grid>
      </Container>

      {/* Footer */}
      <Box sx={{ bgcolor: '#2C1810', py: 4, textAlign: 'center' }}>
        <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontFamily: 'Georgia, serif' }}>
          Powered by{' '}
          <a href="https://bookmylibrary.in" target="_blank" rel="noopener noreferrer" style={{ color: '#D4AF37' }}>
            BookMyLibrary
          </a>
        </Typography>
      </Box>

      {/* Lightbox */}
      <Dialog open={lightboxOpen} onClose={() => setLightboxOpen(false)} maxWidth="lg" fullWidth>
        <IconButton onClick={() => setLightboxOpen(false)} sx={{ position: 'absolute', right: 8, top: 8, zIndex: 1 }}>
          <Close />
        </IconButton>
        <Box component="img" src={images[currentImageIndex]} alt="Gallery" loading="lazy" decoding="async" sx={{ width: '100%', maxHeight: '80vh', objectFit: 'contain' }} />
      </Dialog>
    </Box>
  );
}

export default ClassicTheme;
